import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="text-xl font-medium text-gray-900">BRNIC d.o.o.</div>
            <div className="flex items-center gap-12">
              <button
                onClick={() => scrollToSection('home')}
                className="text-sm font-light text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="text-sm font-light text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-sm font-light text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="text-sm font-light text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-light text-gray-700 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center bg-gradient-to-b from-blue-50/50 via-blue-50/30 to-white pt-20"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-6xl lg:text-7xl font-extralight text-gray-900 leading-tight">
                Modern iOS
                <br />
                Applications for
                <br />
                Enhanced Security
              </h1>
              <p className="text-lg font-light text-gray-600 leading-relaxed max-w-xl">
                Expert team developing VPN and Cleaner solutions to protect and optimize your mobile experience
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-purple-400/20 to-transparent rounded-full blur-3xl"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/8dfd8b1d0d26bf0d945dd07272dee601.jpg"
                alt="iOS Application Interface"
                className="relative z-10 w-full h-auto object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-extralight text-gray-900 mb-4">Our Products</h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* VPN Card */}
            <div className="group bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-12 border border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20modern%20shield%20icon%20with%20lock%20symbol%20gradient%20blue%20purple%20tones%20minimalist%20security%20illustration%20clean%20simple%20design%20flat%20style%20vector%20art%20white%20background&width=200&height=200&seq=vpn001&orientation=squarish"
                    alt="VPN Security"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-4xl font-semibold text-gray-900">VPN</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Secure your connection with military-grade encryption. Browse privately and access content from anywhere in the world with our advanced VPN technology.
                </p>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="group bg-gradient-to-br from-white to-green-50/50 rounded-3xl p-12 border border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-32 h-32 flex items-center justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=abstract%20modern%20cleaning%20broom%20icon%20with%20sparkle%20elements%20gradient%20green%20teal%20tones%20minimalist%20optimization%20illustration%20clean%20simple%20design%20flat%20style%20vector%20art%20white%20background&width=200&height=200&seq=cleaner001&orientation=squarish"
                    alt="Device Cleaner"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-4xl font-semibold text-gray-900">Cleaner</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Optimize your device performance by removing junk files, managing storage, and boosting speed with intelligent cleaning algorithms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-4">
              <h2 className="text-5xl font-extralight text-gray-900 leading-tight">
                About
                <br />
                BRNIC d.o.o.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                BRNIC d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We are committed to delivering innovative solutions that prioritize user privacy, device performance, and seamless functionality. Our dedicated team combines technical expertise with creative design to build applications that users trust and rely on every day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-extralight text-gray-900">Key Features</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                  <i className="ri-shield-check-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Advanced Security</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Military-grade encryption protocols protect your data and online activities. Our security features ensure your privacy is never compromised, giving you peace of mind while browsing.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                  <i className="ri-speed-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Performance Optimization</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Intelligent algorithms analyze and optimize your device performance. Remove unnecessary files, free up storage space, and experience faster app loading times with our optimization tools.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl p-10 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md">
                  <i className="ri-smartphone-line text-4xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">Intuitive Interface</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  User-friendly design makes our applications accessible to everyone. Clean interfaces, smooth animations, and straightforward navigation ensure a delightful user experience every time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Area */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-medium text-white mb-3">BRNIC d.o.o.</h2>
                <p className="text-base text-gray-400">Developing excellence for iOS</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <span className="text-sm text-gray-400">Address: </span>
                    <span className="text-base text-white">Splitska 46</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <span className="text-sm text-gray-400">City: </span>
                    <span className="text-base text-white">Makarska</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <span className="text-sm text-gray-400">Zip: </span>
                    <span className="text-base text-white">21300</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <span className="text-sm text-gray-400">Country: </span>
                    <span className="text-base text-white">Croatia</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-xl font-medium text-white mb-6">Contact Information</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-send-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <div>
                      <span className="text-sm text-gray-400 block mb-1">Email:</span>
                      <a href="mailto:help@brnicdoo.com" className="text-base text-white hover:text-blue-400 transition-colors">
                        help@brnicdoo.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-lg text-blue-400 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <div>
                      <span className="text-sm text-gray-400 block mb-1">Number:</span>
                      <a href="tel:+19789615129" className="text-base text-white hover:text-blue-400 transition-colors">
                        +1 9789615129
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-medium text-white mb-6">Quick Links</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => scrollToSection('home')}
                    className="block text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Home
                  </button>
                  <button
                    onClick={() => scrollToSection('products')}
                    className="block text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Products
                  </button>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="block text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    About
                  </button>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="block text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Features
                  </button>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="block text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-center text-sm text-gray-400">
              © 2025 BRNIC d.o.o. All rights reserved · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">Website Builder</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
